import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Smartphone, RotateCcw, Users, UserCircle } from 'lucide-react';

const MOBILE_DEVICES = {
  'iphone-14': { name: 'iPhone 14', width: 390, height: 844 },
  'iphone-14-pro-max': { name: 'iPhone 14 Pro Max', width: 430, height: 932 },
  'iphone-se': { name: 'iPhone SE', width: 375, height: 667 },
  'pixel-7': { name: 'Pixel 7', width: 412, height: 915 },
  'samsung-s23': { name: 'Samsung S23', width: 360, height: 780 },
};

export const MobileViewWrapper = ({ children }) => {
  const [deviceView, setDeviceView] = useState(() => {
    return localStorage.getItem('deviceViewMode') || 'desktop';
  });
  const [selectedDevice, setSelectedDevice] = useState('iphone-14');
  const [isLandscape, setIsLandscape] = useState(false);
  const [mobileUserType, setMobileUserType] = useState(() => {
    return localStorage.getItem('mobileUserType') || 'staff';
  });
  const location = useLocation();

  // 2026-05-17 — Hard bypass on real mobile devices and installed PWAs.
  // The simulator chrome (Staff/Customer toggle, device dropdown,
  // "Exit Mobile View") is a *desktop preview* tool. On a real iPhone
  // — especially when the user launches the installed PWA from the
  // home screen — it was rendering on top of the actual app and
  // blocking it. Computed once via useState init so hooks order stays stable.
  const [isRealMobileOrPwa] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      if (window.navigator && window.navigator.standalone === true) return true;
      if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
      if (window.matchMedia && window.matchMedia("(display-mode: fullscreen)").matches) return true;
      if (window.matchMedia && window.matchMedia("(display-mode: minimal-ui)").matches) return true;
      const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
      const narrow = (window.innerWidth || 0) < 900;
      if (coarse && narrow) return true;
    } catch { /* ignore */ }
    return false;
  });

  useEffect(() => {
    const handleDeviceViewChange = (e) => {
      setDeviceView(e.detail);
    };

    // Listen for the custom event
    window.addEventListener('deviceViewChange', handleDeviceViewChange);
    
    // Also check localStorage on mount and periodically
    const stored = localStorage.getItem('deviceViewMode');
    if (stored) setDeviceView(stored);
    
    // Check for changes every 500ms as a fallback
    const interval = setInterval(() => {
      const current = localStorage.getItem('deviceViewMode') || 'desktop';
      setDeviceView(prev => prev !== current ? current : prev);
    }, 500);

    return () => {
      window.removeEventListener('deviceViewChange', handleDeviceViewChange);
      clearInterval(interval);
    };
  }, []);

  // Handle mobile user type change
  const toggleMobileUserType = (type) => {
    setMobileUserType(type);
    localStorage.setItem('mobileUserType', type);
    // Dispatch event so other components can react
    window.dispatchEvent(new CustomEvent('mobileUserTypeChange', { detail: type }));
  };

  const exitMobileView = () => {
    setDeviceView('desktop');
    localStorage.setItem('deviceViewMode', 'desktop');
    window.dispatchEvent(new CustomEvent('deviceViewChange', { detail: 'desktop' }));
    window.location.href = '/dashboard';
  };

  // If on mobile app page, auto-enable mobile view mode
  useEffect(() => {
    if (isRealMobileOrPwa) return;  // never auto-enable on real phones / PWAs
    const isOnMobileApp = location.pathname === '/mobile' || location.pathname === '/staff-mobile';
    if (isOnMobileApp && deviceView !== 'mobile') {
      setDeviceView('mobile');
      localStorage.setItem('deviceViewMode', 'mobile');
      // Set the appropriate user type based on which page
      const userType = location.pathname === '/mobile' ? 'customer' : 'staff';
      setMobileUserType(userType);
      localStorage.setItem('mobileUserType', userType);
    }
  }, [location.pathname, isRealMobileOrPwa]);  // eslint-disable-line react-hooks/exhaustive-deps

  // 2026-05-17 — Bypass on real phones / installed PWAs (where the
  // simulator chrome is meaningless and was obscuring the real app).
  if (isRealMobileOrPwa) {
    return children;
  }

  // If desktop view AND not on mobile pages, just render children normally
  if (deviceView !== 'mobile') {
    return children;
  }

  // Check if we're on a mobile app page
  const isOnMobileApp = location.pathname === '/mobile' || location.pathname === '/staff-mobile';
  
  // If mobile view mode but not on mobile app page, render children normally
  if (!isOnMobileApp) {
    return children;
  }

  const device = MOBILE_DEVICES[selectedDevice];
  const frameWidth = isLandscape ? device.height : device.width;
  const frameHeight = isLandscape ? device.width : device.height;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4 overflow-hidden">
      {/* Controls Panel */}
      <div className="fixed top-4 left-4 z-[100] bg-white rounded-lg shadow-xl p-3 space-y-3 min-w-[180px]">
        <div className="flex items-center gap-2 text-slate-700 font-medium text-sm">
          <Smartphone className="w-4 h-4 text-orange-500" />
          Mobile App Preview
        </div>
        
        {/* User Type Toggle */}
        <div className="p-1 bg-slate-100 rounded-lg flex">
          <button
            onClick={() => {
              toggleMobileUserType('staff');
              window.location.href = '/staff-mobile';
            }}
            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
              mobileUserType === 'staff' 
                ? 'bg-blue-500 text-white shadow-sm' 
                : 'text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Users className="w-3 h-3" />
            Staff
          </button>
          <button
            onClick={() => {
              toggleMobileUserType('customer');
              window.location.href = '/mobile';
            }}
            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
              mobileUserType === 'customer' 
                ? 'bg-green-500 text-white shadow-sm' 
                : 'text-slate-600 hover:bg-slate-200'
            }`}
          >
            <UserCircle className="w-3 h-3" />
            Customer
          </button>
        </div>
        
        <select
          value={selectedDevice}
          onChange={(e) => setSelectedDevice(e.target.value)}
          className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-md bg-white"
        >
          {Object.entries(MOBILE_DEVICES).map(([key, dev]) => (
            <option key={key} value={key}>
              {dev.name} ({dev.width}×{dev.height})
            </option>
          ))}
        </select>
        
        <button
          onClick={() => setIsLandscape(!isLandscape)}
          className="w-full flex items-center justify-center gap-2 text-xs px-2 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
          {isLandscape ? 'Portrait' : 'Landscape'}
        </button>
        
        <button
          onClick={exitMobileView}
          className="w-full flex items-center justify-center gap-2 text-xs px-2 py-1.5 bg-slate-800 text-white rounded-md hover:bg-slate-700 transition-colors"
        >
          <X className="w-3 h-3" />
          Exit Mobile View
        </button>
      </div>

      {/* Phone Frame */}
      <motion.div
        layout
        className="relative"
        animate={{ 
          rotate: isLandscape ? 90 : 0,
        }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      >
        {/* Phone bezel */}
        <div 
          className="bg-slate-900 rounded-[3rem] p-3 shadow-2xl"
          style={{
            width: frameWidth + 24,
            height: frameHeight + 24,
          }}
        >
          {/* Notch */}
          <div className="absolute top-6 left-1/2 -translate-x-1/2 w-24 h-6 bg-slate-900 rounded-b-xl z-10" />
          
          {/* Screen */}
          <div 
            className="bg-white rounded-[2.5rem] overflow-hidden relative"
            style={{
              width: frameWidth,
              height: frameHeight,
            }}
          >
            {/* Content - actual mobile app */}
            <div 
              className="absolute inset-0 overflow-auto"
              style={{
                transform: isLandscape ? 'rotate(-90deg)' : 'none',
                transformOrigin: 'center center',
                width: isLandscape ? frameHeight : '100%',
                height: isLandscape ? frameWidth : '100%',
              }}
            >
              {children}
            </div>
            
            {/* Home indicator */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-slate-900 rounded-full z-10" />
          </div>
        </div>
      </motion.div>

      {/* Device info */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur px-4 py-2 rounded-full text-xs text-slate-600 shadow-lg z-[100]">
        {mobileUserType === 'staff' ? '👔 Staff App' : '👤 Customer App'} • {device.name} • {isLandscape ? `${device.height}×${device.width}` : `${device.width}×${device.height}`}
      </div>
    </div>
  );
};

export default MobileViewWrapper;
